import type { ReactNode } from 'react'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { useUi } from '@/stores/RootStore'

interface Props {
  title: string
  note?: string
  /** Кнопки внизу справа от «Закрыть». */
  actions?: ReactNode
  children?: ReactNode
  /** Диалог фиксированной высоты со списком внутри — см. Modal. */
  tall?: boolean | undefined
  /**
   * Спрятать окно, не закрывая диалог. Нужно экранам-спискам (кошельки,
   * автовыводы), когда поверх открывается своё окно — добавление или
   * правка: два окна друг на друге читаются как поломка, и в макете
   * такого нет. Состояние списка живёт: закрылось окно — список вернулся.
   */
  hidden?: boolean | undefined
}

/**
 * Окно кабинета поверх текущей страницы.
 *
 * В макете кошельки, автовыводы, отправка и получение — не отдельные
 * страницы, а диалоги над обзором: под затемнением видно балансы.
 * Это не украшение. Человек, добавляющий адрес вывода, должен видеть,
 * с какого баланса он собирается выводить, — иначе адрес легко
 * привязать не к той монете, а перевод в чужую сеть не возвращается.
 *
 * Какой диалог открыт — состояние (`ui.dialog`), не адрес: страница
 * под окном остаётся той же и не перемонтируется, прокрутка не
 * теряется, а окно всегда по центру экрана.
 */
export function DialogPage({ title, note, actions, children, tall, hidden }: Props) {
  const ui = useUi()
  const close = () => ui.closeDialog()

  if (hidden) return null
  return (
    <Modal title={title} onClose={close} tall={tall}
           {...(note === undefined ? {} : { note })}
           actions={
             <>
               {/* «Закрыть» рисуем здесь, а не в каждом экране:
                   в макете она есть у всех диалогов и всегда первая. */}
               <Button variant="secondary" onClick={close}>Закрыть</Button>
               {actions}
             </>
           }>
      {children}
    </Modal>
  )
}
